import {
  RUNWAY_ENTER_DAYS as SHARED_RUNWAY_ENTER_DAYS,
  RUNWAY_EXIT_DAYS as SHARED_RUNWAY_EXIT_DAYS,
  SEND_RATE_WINDOW_DAYS as SHARED_SEND_RATE_WINDOW_DAYS,
  sendRatePerDay,
  trackRunwayDays,
} from '@workspace/seedforge-shared'
import type { NavBadgeValue } from '@workspace/seedforge-ui/components/app-sidebar'
import {
  FOUNDER_NAV_INVESTOR_CAMPAIGN_LABEL,
  FOUNDER_NAV_REPLIES_LABEL,
  FOUNDER_NAV_SHARE_PROFILE_LABEL,
  FOUNDER_NAV_WARM_INTRO_CAMPAIGN_LABEL,
} from '@workspace/seedforge-ui/lib/founder-nav'

/**
 * Overridable so the badge states can be exercised locally without waiting for a
 * campaign to genuinely drain. Set in `apps/studio-v2/.env`:
 *
 *   VITE_RUNWAY_ENTER_DAYS=40   # amber below this many days
 *   VITE_RUNWAY_EXIT_DAYS=60    # clears above this many days
 *   VITE_SEND_RATE_WINDOW_DAYS=7
 *
 * Defaults are the values in the badge spec. Anything unset or unparseable
 * falls back, so a typo cannot silently disable the badges.
 */
function envDays(key: string, fallback: number): number {
  const raw = import.meta.env?.[key]
  const parsed = raw == null ? Number.NaN : Number(raw)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback
}

/**
 * Runway thresholds. Hysteresis, so a queue hovering near the line cannot
 * flicker. The defaults are the shared ones the Home resolver gates on — the
 * env vars only exist to exercise the states locally, and overriding them makes
 * this menu disagree with Home on purpose.
 */
export const RUNWAY_ENTER_DAYS = envDays('VITE_RUNWAY_ENTER_DAYS', SHARED_RUNWAY_ENTER_DAYS)
export const RUNWAY_EXIT_DAYS = envDays('VITE_RUNWAY_EXIT_DAYS', SHARED_RUNWAY_EXIT_DAYS)

export const SEND_RATE_WINDOW_DAYS = envDays(
  'VITE_SEND_RATE_WINDOW_DAYS',
  SHARED_SEND_RATE_WINDOW_DAYS
)
const MS_PER_DAY = 86_400_000

type Tone = NavBadgeValue['tone']

/**
 * Worst tone wins: one alarm per section.
 *
 * Green ranks lowest — it is the only badge that is good news, so anything
 * needing attention outranks it on the section header. The section still shows
 * green when replies are all there is.
 */
const TONE_RANK: Record<Tone, number> = { emerald: 0, primary: 1, amber: 2, red: 3 }

export function worstTone(tones: Tone[]): Tone | null {
  if (tones.length === 0) return null
  return tones.reduce((worst, tone) => (TONE_RANK[tone] > TONE_RANK[worst] ? tone : worst))
}

/**
 * Trailing send pace, used only until Home's numbers arrive — once they do the
 * badges read those instead (see `home` on NavBadgeInput).
 *
 * The rate itself comes from the shared helper, so the fallback assumes the
 * same pace the Home resolver does. It used to floor at 1/day with no cap,
 * which is how the menu could show a different runway from the card for the
 * same campaign during the load.
 *
 * Returns null when the campaign has never sent: no runway badge then, the Home
 * wizard owns first launch nudging.
 */
export function dailySendRate(
  sentTimestamps: (string | null | undefined)[],
  now: number = Date.now()
): number | null {
  const sent = sentTimestamps.filter((t): t is string => !!t)
  if (sent.length === 0) return null
  const cutoff = now - SEND_RATE_WINDOW_DAYS * MS_PER_DAY
  const recent = sent.filter((t) => new Date(t).getTime() >= cutoff).length
  return sendRatePerDay({ recentDailySendRate: recent / SEND_RATE_WINDOW_DAYS })
}

/**
 * Whether the runway badge should show, given the previous reading. Enters below
 * RUNWAY_ENTER_DAYS and only clears once back above RUNWAY_EXIT_DAYS.
 *
 * Wraps the shared predicate so the env overrides above still apply locally;
 * with them unset this is exactly what the Home resolver gates on.
 */
export function shouldShowRunway(days: number | null, wasShowing: boolean): boolean {
  if (days === null) return false
  return wasShowing ? days <= RUNWAY_EXIT_DAYS : days < RUNWAY_ENTER_DAYS
}

export interface NavBadgeInput {
  /**
   * Runway and unread replies as the Home resolver computed them.
   *
   * The sidebar used to derive both itself, which is how it came to say
   * "0 days left" beside a Home banner reading "3 days" — two calculations of
   * one number. When present these win; the local derivation stays only as the
   * fallback for callers (and tests) that have no Home state to hand.
   */
  home?: {
    runwayDays: number | null
    /**
     * Each campaign's own runway, so a dot answers for its own queue. Home
     * computes both from one send rate split across the running tracks; the
     * sidebar must not re-derive them or the two drift.
     */
    investorRunwayDays?: number | null
    warmIntroRunwayDays?: number | null
    drainedTracks: ('investor' | 'warm_intro')[]
    /** Replies that arrived since the founder last opened the Outcomes page. */
    unseenReplyCount: number
    /**
     * The founder is still setting the campaign up — choosing campaigns,
     * reviewing messages, connecting LinkedIn, launching.
     *
     * No campaign dots during any of it. Home is already telling them the one
     * thing to do next, and a dot on a board they have not launched yet points
     * at a page with nothing on it to act on. Once the campaign is live the
     * dots mean something again.
     */
    inSetup?: boolean
  }
  /** Approved contacts not yet sent — the queue that actually feeds sending. */
  approvedUnsent: number
  /** Connection-request timestamps across the track, used for the send rate. */
  sentTimestamps: (string | null | undefined)[]
  /** Investor campaign is paused, out of budget, or errored. */
  investorStalled: boolean
  /**
   * Each track's paused-by-the-founder flag. A pause is a decision, not a
   * problem: it carries no dot and contributes no runway, because there is
   * nothing for the founder to act on and they already know why it stopped.
   */
  investorPaused?: boolean
  /**
   * The wallet is empty and the trial is over, so sending stopped for a reason
   * approving more contacts cannot fix. Home says top up; a campaign dot beside
   * it is a second, quieter instruction pointing somewhere else.
   */
  outOfFunds?: boolean
  /**
   * The campaign is live. A launched campaign with an empty approved queue is
   * stalled even if it has never sent: it will sit there reaching nobody until
   * the founder approves a batch. Before launch there is nothing to warn about.
   */
  launched?: boolean
  /**
   * Contacts awaiting the founder's decision on the investor track. A runway or
   * drained badge is only raised when this is above zero: with nothing to
   * approve there is no action that clears it, and an unclearable badge is a
   * permanent nag rather than a signal.
   */
  awaitingDecision: number
  /** Warm intro track, same runway rules applied to its own queue. */
  warmIntro?: {
    approvedUnsent: number
    sentTimestamps: (string | null | undefined)[]
    awaitingDecision: number
    /** Campaign is live — same reasoning as `launched` on the investor track. */
    launched?: boolean
    /** Switched off by the founder — see investorPaused. */
    paused?: boolean
  }
  /** Unhandled replies across both campaigns. */
  replyCount: number
  /** Previous runway reading, for hysteresis. */
  wasShowingRunway?: boolean
  now?: number
}

export interface NavBadgeResult {
  badges: Record<string, NavBadgeValue>
  /** Feed back into the next call so the amber state cannot flicker. */
  showingRunway: boolean
}

/**
 * Resolves the Outreach nav badges.
 *
 * A badge may show runway or news, never backlog. Undecided contacts are
 * inventory a founder can legitimately ignore, so they never appear here; they
 * live on the Matched investors page as plain context. What earns a badge: a
 * sending queue about to run dry, one that already has, a stalled campaign, and
 * unhandled replies.
 */
/**
 * What each dot is telling the founder.
 *
 * A bare dot says "something here" and nothing else, which is how a founder
 * ends up approving contacts one at a time hoping the dot notices. Every badge
 * states the condition and the action that clears it.
 */
export const DRAINED_TITLE =
  'Nobody approved is left to send to. Approve more contacts to keep sending.'
export const LOW_TITLE = 'Approved contacts are running low. Approve more to keep sending.'
export const IDLE_TITLE =
  'Nothing approved yet, so this campaign is reaching nobody. Approve contacts to start sending.'
export const STALLED_TITLE = 'This campaign is not sending. Open it to see why.'

function waitingTitle(awaiting: number): string {
  return `${awaiting} ${awaiting === 1 ? 'contact' : 'contacts'} waiting for your approval`
}

/**
 * A campaign's sub-tab badge: the count of contacts waiting for the founder's
 * approval, with the campaign's own state in words when it needs more than that.
 * Null when there is nothing to say.
 */
function campaignBadge(awaiting: number, stateTitle: string | undefined): NavBadgeValue | null {
  if (awaiting === 0 && !stateTitle) return null
  return {
    ...(awaiting > 0 ? { text: String(awaiting) } : {}),
    tone: 'amber',
    title: stateTitle ?? waitingTitle(awaiting),
  }
}

export function resolveOutreachNavBadges(input: NavBadgeInput): NavBadgeResult {
  const {
    approvedUnsent,
    sentTimestamps,
    investorStalled,
    investorPaused = false,
    outOfFunds = false,
    launched = false,
    awaitingDecision,
    warmIntro,
    replyCount,
    home,
    wasShowingRunway = false,
    now,
  } = input

  // Per track: runway only counts when the founder could actually refill it, and
  // a track that is live but has nothing approved is drained even before it has
  // sent, because it is reaching nobody until a batch is approved.
  // Sending capacity belongs to the LinkedIn account, not to a campaign: both
  // tracks push through the same sender, so they draw on one budget. Measure it
  // once across both, then split it across the tracks actually running —
  // otherwise two enabled campaigns each assume the whole allowance and both
  // report roughly double the runway they have.
  const warmRunning =
    !!warmIntro &&
    !warmIntro.paused &&
    (!!warmIntro.launched || warmIntro.sentTimestamps.some(Boolean))
  const investorRunning = !investorPaused && (launched || sentTimestamps.some(Boolean))
  const runningTracks = (investorRunning ? 1 : 0) + (warmRunning ? 1 : 0)

  const combinedRate =
    dailySendRate([...sentTimestamps, ...(warmIntro?.sentTimestamps ?? [])], now) ??
    // Launched but nothing sent yet: fall back to the same floor the rate uses,
    // so one approved contact reads as one day rather than as no reading at all.
    (runningTracks > 0 ? 1 : null)
  const sharedRate = combinedRate === null ? null : combinedRate / Math.max(1, runningTracks)

  const trackState = (approved: number, awaiting: number, isRunning: boolean, hasSent: boolean) => {
    const rate = isRunning ? sharedRate : null
    const canRefill = awaiting > 0
    // A campaign that has just been built has everything awaiting approval and
    // nothing approved — the same shape as one that drained, but it is simply
    // not started. Approving the first batch is the founder's next step and the
    // board says so; a red runway badge on top of that is alarm, not signal. So
    // a track only counts as drained once it has actually sent something.
    const neverStarted = !hasSent && approved === 0
    const drainedNow = canRefill && rate !== null && approved === 0 && hasSent
    return {
      canRefill,
      drained: drainedNow,
      // Live, nothing approved, approvals waiting: the campaign is reaching
      // nobody until the founder acts. Worth a dot on the campaign either way —
      // only the runway text is withheld before the first send, because there
      // is no sending history to measure a runway against.
      idle: canRefill && isRunning && approved === 0,
      // Undefined when there is no rate to divide by, nothing to refill with, or
      // the track has yet to send its first message.
      runwayDays:
        rate === null || !canRefill || drainedNow || neverStarted
          ? null
          : // Already split across the running tracks by `sharedRate`.
            trackRunwayDays({ approved, ratePerDay: rate, runningTracks: 1 }),
    }
  }

  const investor = trackState(
    approvedUnsent,
    awaitingDecision,
    investorRunning,
    sentTimestamps.some(Boolean)
  )
  const warm = warmIntro
    ? trackState(
        warmIntro.approvedUnsent,
        warmIntro.awaitingDecision,
        warmRunning,
        warmIntro.sentTimestamps.some(Boolean)
      )
    : null

  const homeDrained = home?.drainedTracks
  const drained = homeDrained ? homeDrained.includes('investor') : investor.drained
  const warmDrained = homeDrained ? homeDrained.includes('warm_intro') : !!warm?.drained

  // The badge reflects the worse track among those actually running, so a
  // starving campaign cannot hide behind a healthy one.
  const runwayCandidates = [investor.runwayDays, warm?.runwayDays ?? null].filter(
    (d): d is number => d !== null
  )
  const localRunwayDays = runwayCandidates.length ? Math.min(...runwayCandidates) : null
  const runwayDays = home ? home.runwayDays : localRunwayDays
  const anyDrained = homeDrained ? homeDrained.length > 0 : drained || warmDrained
  // Same gate as the per-campaign dots below: "N days left" is a prompt to
  // approve more, and there has to be more to approve for it to be one.
  const canRefillAny = investor.canRefill || !!warm?.canRefill
  const showingRunway =
    !anyDrained && canRefillAny && shouldShowRunway(runwayDays, wasShowingRunway)

  // Home filters replies by the watermark stamped when the founder opens the
  // Outcomes page, so a reply they've already read stops badging the menu. The
  // raw total is the fallback for callers with no Home state.
  const unreadReplies = home ? home.unseenReplyCount : replyCount

  const inSetup = home?.inSetup ?? false

  const badges: Record<string, NavBadgeValue> = {}

  // Each campaign answers for itself. Home's runway is one number for the whole
  // account — both channels send through the same LinkedIn, so its queue is the
  // sum of theirs — and gating the per-track dots on it meant approving a batch
  // on one campaign cleared the dot on the other, which was still starving.
  // The dots read their own track, with the same enter/exit thresholds so
  // neither flickers.
  const investorRunway = home ? (home.investorRunwayDays ?? null) : investor.runwayDays
  const warmRunway = home ? (home.warmIntroRunwayDays ?? null) : (warm?.runwayDays ?? null)
  // `canRefill` is the difference between a warning and a nag: with nothing
  // left awaiting a decision the founder cannot clear this dot however low the
  // runway reads. It is the same gate the drained badge already applies.
  const investorLow =
    !drained && investor.canRefill && shouldShowRunway(investorRunway, wasShowingRunway)
  const warmLow =
    !warmDrained && !!warm?.canRefill && shouldShowRunway(warmRunway, wasShowingRunway)

  const investorState = drained
    ? DRAINED_TITLE
    : investorStalled
      ? STALLED_TITLE
      : investorLow
        ? LOW_TITLE
        : investor.idle
          ? IDLE_TITLE
          : undefined
  const warmState = warmDrained
    ? DRAINED_TITLE
    : warmLow
      ? LOW_TITLE
      : warm?.idle
        ? IDLE_TITLE
        : undefined

  const investorBadge =
    !inSetup && !investorPaused && !outOfFunds
      ? campaignBadge(investorRunning ? awaitingDecision : 0, investorState)
      : null
  if (investorBadge) badges[FOUNDER_NAV_INVESTOR_CAMPAIGN_LABEL] = investorBadge
  const warmBadge =
    !inSetup && !warmIntro?.paused && !outOfFunds
      ? campaignBadge(warmRunning ? (warmIntro?.awaitingDecision ?? 0) : 0, warmState)
      : null
  if (warmBadge) badges[FOUNDER_NAV_WARM_INTRO_CAMPAIGN_LABEL] = warmBadge

  // A finite, clearable news count — the one badge worth chasing. Green, since
  // it is the only one that is good news: everything else in this menu is a
  // problem or a chore, and an investor answering should not look like either.
  if (unreadReplies > 0) {
    badges[FOUNDER_NAV_REPLIES_LABEL] = {
      text: String(unreadReplies),
      tone: 'emerald',
      title: `${unreadReplies} ${unreadReplies === 1 ? 'reply' : 'replies'} you have not read yet.`,
    }
  }

  // The section is a bare dot that inherits the worst child severity, so a
  // collapsed section still shows that something inside needs attention. The
  // words live on the campaign, not on the section.
  // Green is news, not a call to act, so it never lights the section dot.
  const childTone = worstTone(
    Object.values(badges)
      .map((b) => b.tone)
      .filter((tone) => tone !== 'emerald')
  )
  // The section dot stands in for whichever child is worst, so it borrows that
  // child's words rather than inventing a vaguer sentence of its own.
  const worstChildTitle = childTone
    ? (Object.values(badges).find((b) => b.tone === childTone)?.title ?? null)
    : null
  if (childTone) {
    // A campaign's state says what is wrong; its count only says how many wait.
    const stateTitle = [investorState, warmState].find((t) => t !== undefined)
    badges[FOUNDER_NAV_SHARE_PROFILE_LABEL] = {
      tone: childTone,
      title: stateTitle ?? worstChildTitle ?? undefined,
    }
  }

  return { badges, showingRunway }
}
